import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/**
 * A premium, reusable confirmation modal following the Majestic Gold theme.
 */
const MajesticConfirmModal = ({
    show,
    onHide,
    onConfirm,
    title,
    message,
    confirmText,
    cancelText,
    variant = 'danger', // 'danger', 'primary', 'warning'
    icon = 'bi-exclamation-triangle-fill'
}) => {
    const { t } = useTranslation();

    const getVariantColor = () => {
        switch (variant) {
            case 'danger': return 'var(--danger-red)';
            case 'warning': return 'var(--warning-orange)';
            case 'primary': return 'var(--primary-blue)';
            case 'gold': return 'var(--majestic-gold)';
            default: return 'var(--danger-red)';
        }
    };

    const getVariantBg = () => {
        switch (variant) {
            case 'danger': return 'rgba(245, 108, 108, 0.1)';
            case 'warning': return 'rgba(252, 213, 53, 0.1)';
            case 'primary': return 'rgba(64, 158, 255, 0.1)';
            case 'gold': return 'rgba(252, 213, 53, 0.1)';
            default: return 'rgba(245, 108, 108, 0.1)';
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            contentClassName="majestic-modal-glass border-0 shadow-lg"
        >
            <div className="majestic-modal-content" style={{ borderRadius: '24px', overflow: 'hidden' }}>
                <Modal.Body className="text-center py-5 px-4">
                    <div className="confirm-icon-wrapper mb-4">
                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle animate-pulse-slow"
                            style={{
                                width: '80px',
                                height: '80px',
                                background: getVariantBg(),
                                border: `2px solid ${getVariantColor()}33`,
                                boxShadow: `0 0 30px ${getVariantColor()}15`
                            }}>
                            <i className={`bi ${icon}`} style={{ fontSize: '32px', color: getVariantColor() }}></i>
                        </div>
                    </div>

                    <h4 className="fw-bold mb-3" style={{ color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>
                        {title || t('confirm_action', 'Are you sure?')}
                    </h4>

                    <p className="mb-0 text-secondary" style={{ fontSize: '15px', lineHeight: '1.6', opacity: 0.8 }}>
                        {message}
                    </p>
                </Modal.Body>

                <Modal.Footer className="border-0 px-4 pb-4 pt-0 justify-content-center">
                    <div className="d-flex w-100 gap-3">
                        <Button
                            variant="secondary"
                            onClick={onHide}
                            className="flex-grow-1 py-2 fw-bold"
                            style={{
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '12px',
                                color: 'var(--text-regular)',
                                fontSize: '14px',
                                transition: 'all 0.2s'
                            }}
                        >
                            {cancelText || t('cancel', 'Cancel')}
                        </Button>
                        <Button
                            onClick={onConfirm}
                            className="flex-grow-1 py-2 fw-bold shadow-sm"
                            style={{
                                background: variant === 'danger' ? 'var(--danger-red)' : 'linear-gradient(135deg, #fcd535 0%, #f0b90b 100%)',
                                border: 'none',
                                borderRadius: '12px',
                                color: variant === 'danger' ? '#fff' : '#000',
                                fontSize: '14px',
                                transition: 'all 0.2s',
                                boxShadow: variant === 'danger' ? '0 4px 15px rgba(245, 108, 108, 0.3)' : '0 4px 15px rgba(252, 213, 53, 0.3)'
                            }}
                        >
                            {confirmText || t('confirm', 'Confirm')}
                        </Button>
                    </div>
                </Modal.Footer>
            </div>
        </Modal>
    );
};

export default MajesticConfirmModal;
